import React, { useState, useMemo } from 'react'
import './staff-detail.scss'
import { Column, Row, Expaned } from '@/components/layout/layout'
import { useRequest, usePersistFn } from 'ahooks'
import { $post } from '@/api'
import { StaffList } from '../staff-list-table/staff-list-table'
import { getBEMCls } from '@/utils/classname'
import { BaseDrawer } from '@/components/drawer/base-drawer'
import { layerManage, showLayer } from '@/utils/layer'
import { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import Big from 'big.js'
import { IPagination } from '@/model/types/common'
import $msg from '@/utils/message'
import { Table, Tooltip, Spin } from 'antd'
import Button from '@/components/button/button'
import FormTitle from '../../../goods-manage/components/form/form-title/form-title'
import { WithScrollLoad } from '@/utils/utils-widget'
import Iconfont from '@/components/iconfont/iconfont'
import { Modal } from '@/components/dialog/modal'
import SuperAdmin from '@/assets/images/super-admin.svg'
import Auth from '@/views/main/components/auth/auth'
import { shopStore } from '@/store/shop'

interface StaffDetailProps {
    staffId: number
    refresh?: () => void
}

interface WorkRecord {
    staff: number
    shop: number
    start_time: string
    end_time: string
    work_time: string
    cash_payment_amount: number
    balance_payment_amount: number
    ali_payment_amount: number
    wx_payment_amount: number
    cash_refund_amount: number
    balance_refund_amount: number
    ali_refund_amount: number
    wx_refund_amount: number
    cash_recharge_amount: number
    ali_recharge_amount: number
    wx_recharge_amount: number
    custom_payments: any[]
    personal_ali_payment_amount: number
    personal_wx_payment_amount: number
    bank_card_payment_amount: number
    personal_ali_refund_amount: number
    personal_wx_refund_amount: number
    bank_card_refund_amount: number
    personal_ali_recharge_amount: number
    personal_wx_recharge_amount: number
    bank_card_recharge_amount: number
    cashier_over_and_shorts: number
}

const StaffDetail: React.FC<StaffDetailProps> = props => {
    const [staffInfo, setStaffInfo] = useState<StaffList | null>(null)
    const [loading, setLoading] = useState<boolean>(true)

    const cls = (...arg: string[]) => getBEMCls('staff-detail', ...arg)

    const hanldTime = usePersistFn((time: string) => {
        if (!time) {
            return '-'
        }
        const timeArr = time.split(':')
        const hour = timeArr[0]
        const minute = timeArr[1]
        const second = timeArr[2]

        return (+hour ? +hour + '时' : '') + +minute + '分' + +second + '秒'
    })

    useRequest<StaffList>(
        () =>
            $post({
                url: '/staff/getstaffinfo',
                params: {
                    staff_id: props.staffId,
                },
            }),
        {
            onSuccess: res => {
                setStaffInfo(res)
                setLoading(false)
            },
        },
    )

    const deleteStaffReq = useRequest(
        () =>
            $post({
                url: '/staff/deletestaff',
                params: {
                    staff_id: props.staffId,
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                layerManage.pop()
                $msg('success', '删除成功')
                props.refresh && props.refresh()
            },
            onError: e => $msg('error', e.message),
        },
    )

    const columns: ColumnsType<WorkRecord> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '交班时间',
            dataIndex: 'end_time',
            width: 100,
            render: val => (val ? dayjs(val).format('MM-DD HH:MM:ss') : '-'),
        },
        {
            title: '上班时间',
            dataIndex: 'start_time',
            width: 100,
            render: val => (val ? dayjs(val).format('MM-DD HH:MM:ss') : '-'),
        },
        {
            title: '当班时间',
            dataIndex: 'work_time',
            width: 107,
            render: (val: string) => hanldTime(val),
        },
        {
            title: '收款金额',
            render: (val, record) => {
                const { allAmount, wxAmount, aliAmount, cashAmount } = getAmount(record)

                return (
                    <Row data-width-100p data-justify-content-space-between>
                        <div>{allAmount}</div>
                        <Tooltip
                            placement="bottomLeft"
                            title={
                                <Row data-color-333 data-align-items-center>
                                    微信：{wxAmount}
                                    <div className={cls('line')} />
                                    支付宝：{aliAmount}
                                    <div className={cls('line')} />
                                    现金：{cashAmount}
                                </Row>
                            }
                        >
                            <div data-color-999 className={cls('pay-detail')}>
                                微信：{wxAmount} | 支付宝：{aliAmount} | 现金：{cashAmount}
                            </div>
                        </Tooltip>
                    </Row>
                )
            },
        },
        {
            title: '收银长短款',
            dataIndex: 'cashier_over_and_shorts',
            width: 110,
            render: val => (
                <div
                    style={{
                        color: val < 0 ? '#FF4343' : '#000',
                    }}
                >
                    {val}
                </div>
            ),
        },
    ]

    const getAmount = (record: any) => {
        const getAllPayment = (payTypeArr: string[]) =>
            payTypeArr.reduce((pre, next) => {
                const getItemCount = (i: string) => record[next + i] || 0

                return +Big(pre)
                    .plus(getItemCount('payment_amount'))
                    .plus(getItemCount('recharge_amount'))
                    .minus(getItemCount('refund_amount'))
            }, 0)

        return {
            allAmount: +Big(
                getAllPayment(['cash_', 'balance_', 'bank_card_', 'ali_', 'wx_', 'personal_ali_', 'personal_wx_']),
            ).plus(getAllPayment(record.custom_payments)),
            wxAmount: getAllPayment(['wx_', 'personal_wx_']),
            aliAmount: getAllPayment(['ali_', 'personal_ali_']),
            cashAmount: getAllPayment(['cash_']),
        }
    }

    const requestHandle = useRequest<
        {
            list: WorkRecord[]
            pagination: IPagination
        },
        {
            work_records_list: WorkRecord[]
            pagination: IPagination
        }
    >(
        val => {
            return $post({
                url: '/staff/getworkrecord',
                params: {
                    limit: 20,
                    page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                    staff_id: props.staffId,
                },
            })
        },
        {
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            loadMore: true,
            formatResult: data => {
                return {
                    list: data.work_records_list,
                    pagination: data.pagination,
                }
            },
            onError: e => {
                $msg('error', e.message)
            },
        },
    )

    const isAdmin = useMemo(() => shopStore.shopInfo?.creator.id === staffInfo?.user.id, [staffInfo])

    return (
        <BaseDrawer
            isShow
            title="员工详情"
            width={736}
            onClose={() => {
                layerManage.pop()
            }}
        >
            <Column data-align-items-center data-height-100p>
                <Expaned data-overflow-auto>
                    <Spin spinning={loading}>
                        <Column data-align-items-center>
                            <div data-position-relative>
                                <img
                                    className={cls('avatar')}
                                    style={{
                                        border: `1px solid ${staffInfo?.user.gender === 2 ? '#ee5b5b' : '#3c90ff'}`,
                                    }}
                                    alt=""
                                    src={staffInfo?.user.avatar}
                                />
                                {isAdmin && <img alt="" src={SuperAdmin} className={cls('badge')} />}
                            </div>

                            <Row>
                                <div className={cls('nickname')}>{staffInfo?.user.nickname}</div>
                                <div className={cls('realname')}>({staffInfo?.user.realname})</div>
                            </Row>
                            <Row data-margin-top-10>
                                <Row data-align-items-center>
                                    <Iconfont name="icon5shengri" size={16} />
                                    <div className={cls('birthday')}>{staffInfo?.user.birthday || '-'}</div>
                                </Row>
                                <Row data-align-items-center data-margin-left-8>
                                    <Iconfont name="icon5shoujihao" size={16} />
                                    <div className={cls('birthday')}>{staffInfo?.user.phone}</div>
                                </Row>
                            </Row>
                            <Row data-margin-top-10>
                                <Row data-align-items-center data-color-999 data-font-size-14>
                                    <div className={cls('des-title')}>入职时间</div>：
                                    <div data-color-333>{staffInfo?.entry_date || '未设置'}</div>
                                </Row>
                                <Row data-align-items-center data-color-999 data-font-size-14 data-margin-left-32>
                                    <div className={cls('des-title')}>备注</div>：
                                    <div data-color-ff4343>{staffInfo?.remarks || '未设置'}</div>
                                </Row>
                            </Row>
                        </Column>
                        <FormTitle hasLine={false} title="交接班记录" />
                        <div className={cls('table')}>
                            <WithScrollLoad data-expaned data-overflow-hidden scrollloadhandle={requestHandle}>
                                <Table
                                    rowKey={record => record.start_time}
                                    loading={requestHandle.loading || requestHandle.loadingMore || void 0}
                                    bordered
                                    pagination={false}
                                    columns={columns}
                                    dataSource={requestHandle.data?.list}
                                    scroll={{ x: 'max-content', y: 258 }}
                                />
                            </WithScrollLoad>
                        </div>
                    </Spin>
                </Expaned>
                <div className={cls('bottom')} hidden={isAdmin}>
                    <Row>
                        <Auth
                            perssion={22}
                            onClick={() => {
                                showLayer(
                                    <Modal
                                        headerStyles={{ width: '440px' }}
                                        isShow
                                        type="danger"
                                        style={{ height: '156px' }}
                                        onClose={() => layerManage.pop()}
                                        onCancel={() => layerManage.pop()}
                                        onConfirm={() => {
                                            deleteStaffReq.run()
                                            layerManage.pop()
                                        }}
                                    >
                                        <div>
                                            <Row
                                                data-align-items-center
                                                data-font-size-20
                                                data-font-weight-600
                                                data-color-000
                                            >
                                                确认删除员工{' '}
                                                <div data-color-danger data-margin-h-6>
                                                    {staffInfo?.user.realname}
                                                </div>{' '}
                                                吗？
                                            </Row>
                                            <div data-font-size-14 data-color-999 data-margin-top-8>
                                                删除仍会保留员工历史收银记录
                                            </div>
                                        </div>
                                    </Modal>,
                                )
                            }}
                        >
                            <Button types="error" loading={deleteStaffReq.loading || void 0}>
                                删除
                            </Button>
                        </Auth>
                    </Row>
                </div>
            </Column>
        </BaseDrawer>
    )
}

export default StaffDetail
